import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { waitFor, onMount } from 'lp-hoc'
import { InfoModal, Modal, LoadingSpinner } from 'components'
import ProgramBlade from '../../profile/components/program-blade'
import { ButtonArea } from 'lp-components'
import { selectors as globalSelectors } from 'global-reducer'
import { selectors as partnerSelectors } from '../../profile/reducer'
import * as apiActions from 'api-actions'
import * as Types from 'types'
import { useDynamicInfoModal } from 'utils'

const propTypes = {
  programs: PropTypes.arrayOf(PropTypes.object).isRequired,
  onProgramsChange: PropTypes.func.isRequired,
  onSubmitApplication: PropTypes.func.isRequired,
  grades: PropTypes.arrayOf(Types.grade).isRequired,
  programTypes: PropTypes.arrayOf(Types.programType).isRequired,
  outcomeTypes: PropTypes.arrayOf(Types.outcomeType).isRequired,
  approaches: PropTypes.arrayOf(Types.communityEnumerable).isRequired,
  disciplines: PropTypes.arrayOf(Types.discipline).isRequired,
  languages: PropTypes.arrayOf(Types.language).isRequired,
  financialAssistanceTypes: PropTypes.arrayOf(Types.communityEnumerable)
    .isRequired,
  identityFrequencies: PropTypes.arrayOf(Types.communityEnumerable).isRequired,
  applicationSubmitting: PropTypes.bool,
}

const defaultProps = {
  applicationSubmitting: false,
}

function ApplicationFormStep3({
  programs,
  onProgramsChange,
  onSubmitApplication,
  grades,
  programTypes,
  outcomeTypes,
  approaches,
  disciplines,
  languages,
  financialAssistanceTypes,
  identityFrequencies,
  applicationSubmitting,
}) {
  const [submitPending, setSubmitPending] = useState(false)
  const [showAddProgramModal, setShowAddProgramModal] = useState(false)
  const [showInfoModal, infoModalContent, setShowInfoModalWithContent] =
    useDynamicInfoModal()

  const openAddModal = useCallback(() => setShowAddProgramModal(true), [])
  const closeAddModal = useCallback(() => setShowAddProgramModal(false), [])

  const handleAddProgram = useCallback(
    (params) => {
      const clientId = `draft-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 9)}`
      const id = Date.now()
      onProgramsChange((prev) => [...prev, { ...params, clientId, id }])
      return Promise.resolve()
    },
    [onProgramsChange]
  )

  const handleUpdateProgram = useCallback(
    (program) => (params) => {
      onProgramsChange((prev) =>
        prev.map((p) =>
          p.clientId === program.clientId
            ? { ...params, clientId: p.clientId, id: p.id }
            : p
        )
      )
      return Promise.resolve()
    },
    [onProgramsChange]
  )

  const handleDeleteProgram = useCallback(
    (program) => () => {
      onProgramsChange((prev) =>
        prev.filter((p) => p.clientId !== program.clientId)
      )
      return Promise.resolve()
    },
    [onProgramsChange]
  )

  const hasPrograms = programs.length > 0

  return (
    <div className="application-step3">
      <h2 className="application-programs-lead">
        Add one or more programs your organization provides
        <span className="application-field-required" aria-hidden="true">
          {' '}
          *
        </span>
      </h2>
      <button type="button" className="button-secondary" onClick={openAddModal}>
        + Add a new program
      </button>

      {hasPrograms && (
        <div className="application-programs-list">
          <div className="application-programs-list-header">Programs</div>
          <div className="application-programs-cards">
            <div className="expandable-section program-expander application-program-expander">
              {programs.map((program) => (
                <div
                  key={program.clientId}
                  className="application-program-card"
                >
                  <ProgramBlade
                    program={program}
                    onSubmit={handleUpdateProgram(program)}
                    onSubmitSuccess={() => {}}
                    onDelete={handleDeleteProgram(program)}
                    setShowInfoModalWithContent={setShowInfoModalWithContent}
                    disabled={false}
                    grades={grades}
                    programTypes={programTypes}
                    outcomeTypes={outcomeTypes}
                    financialAssistanceTypes={financialAssistanceTypes}
                    identityFrequencies={identityFrequencies}
                    approachTypes={approaches}
                    disciplines={disciplines}
                    languages={languages}
                    isNewProgram={false}
                    partnerId={null}
                    isApplication
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showAddProgramModal && (
        <Modal onClose={closeAddModal}>
          <div className="modal-title">Add a new program</div>
          <ProgramBlade
            isNewProgram
            onSubmit={handleAddProgram}
            onSubmitSuccess={closeAddModal}
            onDelete={closeAddModal}
            setShowInfoModalWithContent={setShowInfoModalWithContent}
            disabled={false}
            grades={grades}
            programTypes={programTypes}
            outcomeTypes={outcomeTypes}
            financialAssistanceTypes={financialAssistanceTypes}
            identityFrequencies={identityFrequencies}
            approachTypes={approaches}
            disciplines={disciplines}
            languages={languages}
            partnerId={null}
            isApplication
          />
        </Modal>
      )}

      {showInfoModal && (
        <InfoModal
          onClose={() => setShowInfoModalWithContent(false)}
          content={infoModalContent.content}
          header={infoModalContent.header}
        />
      )}

      <ButtonArea>
        <button
          type="button"
          className="full-width button-secondary"
          disabled={!hasPrograms || applicationSubmitting || submitPending}
          onClick={() => {
            if (submitPending) return
            setSubmitPending(true)
            Promise.resolve(onSubmitApplication(programs)).finally(() => {
              setSubmitPending(false)
            })
          }}
        >
          {applicationSubmitting || submitPending
            ? 'Submitting…'
            : 'Submit Application'}
        </button>
      </ButtonArea>
    </div>
  )
}

ApplicationFormStep3.propTypes = propTypes
ApplicationFormStep3.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    disciplines: globalSelectors.definedDisciplines(state),
    programTypes: globalSelectors.orderedProgramTypes(state),
    outcomeTypes: globalSelectors.outcomeTypes(state),
    grades: globalSelectors.grades(state),
    approaches: partnerSelectors.approaches(state),
    languages: globalSelectors.languages(state),
    financialAssistanceTypes: partnerSelectors.financialAssistanceTypes(state),
    identityFrequencies: partnerSelectors.identityFrequencies(state),
  }
}

const mapDispatchToProps = {
  fetchGrades: apiActions.fetchGrades,
  fetchOutcomeTypes: apiActions.fetchOutcomeTypes,
  fetchProgramTypes: apiActions.fetchProgramTypes,
  fetchLanguages: apiActions.fetchLanguages,
  fetchFinancialAssistanceTypes: apiActions.fetchFinancialAssistanceTypes,
  fetchIdentityFrequencies: apiActions.fetchIdentityFrequencies,
  fetchApproaches: apiActions.fetchApproaches,
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  onMount('fetchGrades'),
  onMount('fetchOutcomeTypes'),
  onMount('fetchProgramTypes'),
  onMount('fetchLanguages'),
  onMount('fetchFinancialAssistanceTypes'),
  onMount('fetchIdentityFrequencies'),
  onMount('fetchApproaches'),
  waitFor(
    [
      'grades',
      'outcomeTypes',
      'programTypes',
      'languages',
      'financialAssistanceTypes',
      'identityFrequencies',
      'approaches',
    ],
    LoadingSpinner
  )
)(ApplicationFormStep3)
